import { TrendingUp } from "lucide-react";
import { Delta } from "./delta";
import { cn } from "@/lib/utils";
import { fmtComparacao, fmtDataCurtaBR, fmtDiaCurto, fmtMesCurto, fmtMesLongo, fmtMoedaBR } from "@/lib/formato-mercado";
import { labelReferencial } from "@shared/schema";
import type { CotacaoPainelDTO, DestaqueSeuPrecoDTO } from "@shared/mercado-inicio";

/**
 * "Cotação da praça · hoje" — O MESMO CARD no Início e em Cotações do comprador.
 *
 * Uma linha por produto: o preço de hoje, a variação "vs dd/mm" (contra a última
 * semana publicada NA MESMA BASE — a regra é do servidor), o gráfico das 8
 * semanas e, só em Cotações, "seu preço médio · {mês}".
 *
 * ⚠️ `seuPrecoMedio` é OPCIONAL no DTO, e é a presença da chave que liga a
 * coluna. O Início não a recebe (decisão do #237: o painel de operação mostra a
 * placa, e só); duas cópias deste card divergiriam na primeira mudança de
 * espaçamento, e o comprador passa de uma tela para a outra com um toque.
 *
 * Nada é calculado aqui: preço, variação, média e a porcentagem da frase do
 * rodapé chegam prontos.
 */

/**
 * As 8 semanas, com a placa de cada uma.
 *
 * ⚠️ A LINHA LIGA SÓ OS PONTOS AO PRODUTOR. O ponto de preço de venda sai
 * VAZADO e solto: ligá-lo desenharia um pico de R$ 1,90 no meio de uma série de
 * R$ 1,40 — que é a distância entre duas placas, não um movimento de preço.
 */
export function SparklineDaPraca({ valores, referenciais }: { valores: number[]; referenciais: string[] }) {
  // Menos de 3 pontos não vira gráfico: com dois a linha sempre parece tendência.
  if (valores.length < 3) return null;
  const W = 140, H = 34, pad = 4;
  const max = Math.max(...valores);
  const min = Math.min(...valores);
  const x = (i: number) => pad + (i * (W - 2 * pad)) / (valores.length - 1);
  const y = (v: number) => H - pad - (max === min ? 0.5 : (v - min) / (max - min)) * (H - 2 * pad);
  const ehVenda = (i: number) => referenciais[i] === "VENDA";
  let d = "";
  valores.forEach((v, i) => {
    if (ehVenda(i)) return;
    d += `${d ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`;
  });
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-[140px] h-[34px] block"
      role="img"
      aria-label={`8 semanas: de ${fmtMoedaBR(min)} a ${fmtMoedaBR(max)}`}
      data-testid="sparkline-praca"
    >
      {d && <path d={d} fill="none" stroke="hsl(var(--chart-1))" strokeWidth={1.8} />}
      {valores.map((v, i) => (
        <circle
          key={i}
          cx={x(i).toFixed(1)}
          cy={y(v).toFixed(1)}
          r={i === valores.length - 1 ? 3 : 2}
          fill={ehVenda(i) ? "hsl(var(--card))" : "hsl(var(--chart-1))"}
          stroke="hsl(var(--chart-1))"
          strokeWidth={ehVenda(i) ? 1.2 : 0}
          data-venda={ehVenda(i) ? "" : undefined}
        />
      ))}
    </svg>
  );
}

/** "Seu preço médio na Prata 1ª em setembro ficou 16,1% acima da praça de hoje." */
export function fraseDoSeuPreco(d: DestaqueSeuPrecoDTO, mes: string | undefined): { antes: string; destaque: string; depois: string } {
  const v = d.comparacao.valor;
  const antes = `Seu preço médio na ${d.produto} em ${fmtMesLongo(mes)} ficou `;
  if (Math.abs(v) < 0.05) return { antes, destaque: "igual", depois: " à praça de hoje." };
  const pct = fmtComparacao({ tipo: d.comparacao.tipo, valor: Math.abs(v) });
  return { antes, destaque: `${pct} ${v > 0 ? "acima" : "abaixo"}`, depois: " da praça de hoje." };
}

export function CardCotacaoDaPraca({
  dados, titulo, comPracaNaMeta, acao, testId = "card-cotacao",
}: {
  dados: CotacaoPainelDTO;
  titulo: string;
  /** Início: "Mirorós · seg 21/09 · …". Em Cotações a praça já está no título. */
  comPracaNaMeta?: boolean;
  /** O que vai à direita do rodapé (o "Histórico →" do Início). */
  acao?: React.ReactNode;
  testId?: string;
}) {
  const meta = [
    comPracaNaMeta ? dados.praca?.nome : null,
    dados.publicadaEm ? fmtDiaCurto(dados.publicadaEm) : null,
    dados.referencial ? labelReferencial(dados.referencial) : null,
    dados.fonte,
  ].filter(Boolean).join(" · ");
  const comSeuPreco = dados.linhas.some(l => l.seuPrecoMedio !== undefined);

  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid={testId}>
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b px-3 sm:px-4 py-2.5">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />{titulo}
        </h3>
        {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
      </header>

      {dados.linhas.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <TrendingUp className="h-6 w-6 mx-auto text-muted-foreground" />
          <p className="mt-2 text-sm font-medium">{`A cotação da praça ${dados.praca?.nome ?? ""} ainda não foi publicada hoje.`}</p>
          <p className="mt-1 text-xs text-muted-foreground max-w-[46ch] mx-auto">
            {dados.publicadaEm
              ? `A última publicação foi em ${fmtDataCurtaBR(dados.publicadaEm)}. Assim que sair a do dia, ela aparece aqui.`
              : "Assim que a praça publicar o preço do dia, ele aparece aqui."}
          </p>
        </div>
      ) : (
        <ul className="divide-y">
          {dados.linhas.map(l => (
            <li
              key={l.produto}
              className={cn(
                "px-3 sm:px-4 py-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1",
                comSeuPreco
                  ? "sm:grid-cols-[minmax(0,1.1fr)_minmax(130px,1fr)_140px_minmax(0,0.9fr)] sm:gap-x-4"
                  : "sm:grid-cols-[minmax(0,1.1fr)_minmax(130px,1fr)_140px] sm:gap-x-4",
              )}
              data-testid={`cotacao-${l.produto}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{l.produto}</p>
                <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-muted-foreground whitespace-nowrap">
                  <Delta value={l.variacaoSemanaPct} testId={`variacao-${l.produto}`} />
                  {l.comparadoCom ? `vs ${fmtDataCurtaBR(l.comparadoCom)}` : "sem semana na mesma base"}
                </p>
                {comSeuPreco && (
                  <p className="sm:hidden mt-1 text-[11.5px] text-muted-foreground whitespace-nowrap">
                    seu preço médio · {fmtMesCurto(dados.mesSeuPreco)}{" "}
                    <b className="font-mono text-foreground">{l.seuPrecoMedio != null ? fmtMoedaBR(l.seuPrecoMedio) : "—"}</b>
                  </p>
                )}
              </div>
              <p className="font-mono text-lg sm:text-[22px] font-semibold tabular-nums whitespace-nowrap">
                {fmtMoedaBR(l.precoKg)}<span className="ml-1 text-[11px] font-normal text-muted-foreground font-sans">/kg</span>
              </p>
              {/* O gráfico some no celular: 8 semanas em 390px viram um borrão. */}
              <div className="hidden sm:block">
                <SparklineDaPraca valores={l.serie8} referenciais={l.serie8Referencial} />
              </div>
              {comSeuPreco && (
                <div className="hidden sm:block text-right text-xs text-muted-foreground" data-testid={`seu-preco-${l.produto}`}>
                  seu preço médio · {fmtMesCurto(dados.mesSeuPreco)}
                  <b className="block font-mono text-[15px] font-semibold text-foreground tabular-nums">
                    {l.seuPrecoMedio != null ? fmtMoedaBR(l.seuPrecoMedio) : "—"}
                  </b>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/40 px-3 sm:px-4 py-2 text-xs text-muted-foreground">
        <div className="space-y-0.5">
          {dados.destaqueSeuPreco && (() => {
            const f = fraseDoSeuPreco(dados.destaqueSeuPreco, dados.mesSeuPreco);
            return (
              <p className="text-foreground/80" data-testid="frase-seu-preco">
                {f.antes}<b>{f.destaque}</b>{f.depois}
              </p>
            );
          })()}
          <p>Variação contra a última semana publicada na mesma base.</p>
        </div>
        {acao}
      </footer>
    </section>
  );
}
